import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import TypingBubble from "../components/TypingBubble";
import { Brain, Sparkles, Wind, Moon, ArrowRight } from "lucide-react";

const MOODS = ["Fired up", "Calm", "Nervous", "Focused", "Tired", "Frustrated", "Confident", "Distracted"];
const FOCUS = [
  { id: "Game Prep", icon: Brain }, { id: "Focus", icon: Sparkles },
  { id: "Calm", icon: Wind }, { id: "Sleep Routine", icon: Moon },
];

export default function CheckIn() {
  const navigate = useNavigate();
  const [mood, setMood] = useState("");
  const [intention, setIntention] = useState("");
  const [focus, setFocus] = useState("Focus");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/checkin", { mood, intention, focus });
      setResult(data);
    } finally { setBusy(false); }
  };

  return (
    <div className="relative flex-1 overflow-y-auto mm-scroll bg-white mm-glow" data-testid="checkin-page">
      <div className="relative z-10 px-5 pt-7 pb-8">
        <h1 className="font-serif text-3xl font-bold text-navy">Daily check-in</h1>
        <p className="text-slate-500 mt-1">A quick pulse — then I'll point you somewhere good.</p>

        {!result ? (
          <>
            <div className="mt-7">
              <div className="text-xs font-bold uppercase tracking-wider text-royal mb-2.5">How are you feeling?</div>
              <div className="flex flex-wrap gap-2">
                {MOODS.map((m) => (
                  <button key={m} data-testid={`mood-${m.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setMood(m)}
                    className={`px-4 py-2.5 rounded-full text-sm font-semibold border transition active:scale-95 ${
                      mood === m ? "bg-royal text-white border-royal" : "bg-white text-navy border-slate-300"}`}>{m}</button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <div className="text-xs font-bold uppercase tracking-wider text-royal mb-2.5">How do you plan to show up today?</div>
              <textarea data-testid="checkin-intention" value={intention} onChange={(e) => setIntention(e.target.value)} rows={3}
                placeholder="I'll stay present and trust my prep…"
                className="w-full px-4 py-3 rounded-2xl bg-slate-100 border border-transparent focus:border-royal focus:bg-white outline-none resize-none" />
            </div>

            <div className="mt-6">
              <div className="text-xs font-bold uppercase tracking-wider text-royal mb-2.5">What do you need?</div>
              <div className="grid grid-cols-2 gap-2.5">
                {FOCUS.map((f) => (
                  <button key={f.id} data-testid={`checkin-focus-${f.id.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setFocus(f.id)}
                    className={`flex items-center gap-2.5 rounded-2xl border p-3.5 font-semibold transition active:scale-95 ${
                      focus === f.id ? "bg-royal/10 border-royal text-royal" : "bg-white border-slate-200 text-navy"}`}>
                    <f.icon size={18} /> {f.id}
                  </button>
                ))}
              </div>
            </div>

            <button data-testid="checkin-submit" disabled={!mood || busy} onClick={submit}
              className="mt-7 w-full bg-royal text-white font-bold py-4 rounded-2xl disabled:opacity-40 active:scale-[0.98] transition">
              {busy ? "Checking in…" : "Check in"}
            </button>
          </>
        ) : (
          <div className="mt-8 space-y-4">
            <TypingBubble text={result.reply} testId="checkin-reply" />
            {result.recommended ? (
              <button data-testid="checkin-recommended" onClick={() => navigate(`/player/${result.recommended.content_id}`)}
                className="w-full text-left rounded-2xl bg-navy text-white p-5 active:scale-[0.99] transition">
                <div className="text-turq text-xs font-bold uppercase tracking-widest">Recommended for you</div>
                <div className="font-serif text-xl font-bold mt-1.5">{result.recommended.title}</div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold">Open session <ArrowRight size={15} /></div>
              </button>
            ) : (
              <button data-testid="checkin-golibrary" onClick={() => navigate("/library")}
                className="w-full bg-slate-100 text-navy font-semibold py-3.5 rounded-2xl">Browse your library</button>
            )}
            <button data-testid="checkin-again" onClick={() => { setResult(null); setMood(""); setIntention(""); }}
              className="w-full text-slate-400 text-sm py-2">New check-in</button>
          </div>
        )}
      </div>
    </div>
  );
}
